import React, { Component } from 'react';
import { connect, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import {
  Button,
  Dropdown,
  DropdownCheckboxItem,
  DropdownItem,
  DropdownItemWithConfirmation,
  DropdownList,
  Island,
  IslandButton,
  Label,
  Tooltip,
  TrevorIcon,
} from '@trevor/ui-components';
import { post, userAction } from '@/util/helpers/ajax-helpers';
import { useDatasourceId } from '@/hooks';
import { getDashboardRefreshId } from '@/store-selectors';
import styled from 'styled-components';
import { useModal } from '@/features/modals/modalSlice';
import { modalHidden, reloadDashboardFilters } from '@/actions';
import { useAppDispatch } from '@/store/hooks';
import { DashboardFilterModal } from '@/features/dashboard/components/DashboardFilterModal';
import { DashboardFilterSavedQueryPresets } from '@/components/DashboardFilterSavedQueryPresets';
import DashboardCustomFilterModal from '@/features/dashboard/components/DashboardCustomFilterModal';
import { usePrefetchSavedQueries } from '@/features/savedQueriesWidget/apiSlice';
import style from '../css/module/dashboard-filters.css';
import buttons from '../css/module/buttons.css';
import flex from '../css/module/flex.css';
import LayerWhere from './layers/LayerWhere';
import { EQ, LESS_THAN, ONE_OF } from '../util/Operators';
import AggregateColumn from '../util/AggregateColumn';
import { DAY, MONTH, WEEK, YEAR } from '../util/enums/EnumDateTimeAggregation';
import { BOOL, DATE, FLOAT, INT, STRING, UUID } from '../util/ColumnType';
import {
  LAST_MONTH,
  LAST_WEEK,
  LAST_YEAR,
  THIS_MONTH,
  THIS_WEEK,
  THIS_YEAR,
  TODAY,
  YESTERDAY,
} from './modals/ModalWhere';
import CopyIcon from './CopyIcon';
import { COPY_LINK, DASHBOARD_FILTER } from '../constants/test-ids';

const FilterListContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
`;

const DropdownArrow = styled(TrevorIcon).attrs({
  name: 'chevron-down',
})`
  margin-left: var(--spacing-s);
`;

const FilterLabel = styled.div`
  color: var(--color-interface-content-normal);
  display: flex;
  gap: calc(var(--spacing-xxs) * 2);
  margin-right: var(--spacing-l);
`;

class DashboardFilters_LEGACY extends Component {
  constructor(props) {
    super(props);
    this.state = {
      multiFilters: {}, // an object whose keys are filter names and values are arrays of selected filter preset values
      dropdownOpen: null, // filter.name() whose dropdown is open
      filtersLoaded: true, // Flag used to control that missing filters notification is shown only once
    };

    this.clearFilter = this.clearFilter.bind(this);
    this.updateMultiFilter = this.updateMultiFilter.bind(this);
    this.onToggleDropdown = this.onToggleDropdown.bind(this);
  }

  componentDidMount() {
    this.props.onReloadFilters();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.filtersLoaded && prevState.filtersLoaded) {
      this.setState({
        filtersLoaded: false,
      });
    }
  }

  onToggleDropdown(filter, open) {
    const { multiFilters, dropdownOpen } = this.state;

    // if dashboard is opened, add filter name to state
    if (open) {
      this.setState({
        dropdownOpen: filter.name(),
      });
    } else if (dropdownOpen === filter.name()) {
      // dashboard is closed
      // clear state
      this.setState({
        dropdownOpen: null,
      });
      // if the dropdown just closed has selected multiFilter values, update filters.
      if (filter.name() in multiFilters) {
        this.submitMultiSelectFilter(filter);
      }
    }
  }

  submitMultiSelectFilter(filter) {
    const { appliedFilters } = this.props;
    const { multiFilters } = this.state;
    const layerPosition = appliedFilters.findIndex(
      (l) => l.lhs().name() === filter.name(),
    );

    // the selected values from the dropdown inputs
    const newRHSArray = multiFilters[filter.name()]
      ? multiFilters[filter.name()].slice()
      : [];

    const existingRHSArray =
      layerPosition === -1
        ? []
        : appliedFilters[layerPosition].rhsValue().split('\n');

    // No filters added or removed, so return
    if (newRHSArray.length === 0 && existingRHSArray.length === 0) {
      return;
    }

    // All filters removed
    if (newRHSArray.length === 0) {
      this.clearFilter(layerPosition);
      return;
    }

    // Add new filter.
    const rhsValue = newRHSArray.join('\n');
    const lhs = AggregateColumn.fromColumn(filter.asColumn().name(), null);
    const newLayer = new LayerWhere(
      lhs,
      ONE_OF,
      rhsValue,
      null,
      filter.columnType(),
    );

    // New filter has been added
    if (newRHSArray.length > 0 && existingRHSArray.length === 0) {
      this.props.onApplyFilter(newLayer);
    }
  }

  clearFilter(i, filterName) {
    const { multiFilters } = this.state;
    const { appliedFilters, onUpdated } = this.props;
    const newFilters = appliedFilters.slice(); // take a copy

    if (filterName && filterName in multiFilters) {
      this.setState({
        multiFilters: { ...multiFilters, [filterName]: [] },
      });
    }
    newFilters.splice(i, 1); // remove item
    onUpdated(newFilters);
  }

  // update state with multifilter selections (these will later be sent to server)
  updateMultiFilter(f, preset, value) {
    const { multiFilters } = this.state;
    const copyOfObj = { ...multiFilters };

    // check if filter name exists in object. If it does, copy its value (an array). Otherwise, create a new array
    const presetsArray =
      f.name() in copyOfObj ? copyOfObj[f.name()].slice() : [];

    // if the value is false, remove the preset from the array. Otherwise, add it to the array
    if (!value) {
      const index = presetsArray.indexOf(preset);
      presetsArray.splice(index, 1);
    } else {
      presetsArray.push(preset);
    }
    // assign the array to the filter name.
    copyOfObj[f.name()] = presetsArray;

    this.setState({
      multiFilters: copyOfObj,
    });
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  updateState(state, value) {
    this.setState({
      [state]: value,
    });
  }

  filterWithPresetsDropdownItems(f) {
    const { multiFilters } = this.state;

    const presetsSelected =
      multiFilters[f._name] && multiFilters[f._name].length > 0;

    const menuItems = f
      .presets()
      .filter((preset) => preset.length > 0) // remove blank presets
      .map((preset) => {
        const value =
          f.name() in multiFilters
            ? multiFilters[f._name].includes(preset)
            : false;

        return (
          <DropdownCheckboxItem
            key={`dropdown-item-${f._id}-${preset}`}
            selected={value}
            label={preset}
            onClick={() => this.updateMultiFilter(f, preset, !value)}
          />
        );
      });

    if (menuItems.length === 0) {
      menuItems.push(
        <DropdownItem key="no-presets" disabled>
          no preset choices configured
        </DropdownItem>,
      );
    }

    return (
      <>
        <DropdownItem
          disabled={presetsSelected}
          topSticky
          onClick={() => this.props.onCustomiseFilterOpen(f.asColumn())}
        >
          Custom
        </DropdownItem>
        {menuItems}
        {presetsSelected && (
          <DropdownItem
            bottomSticky
            onClick={() => this.submitMultiSelectFilter(f)}
          >
            <Button size="small" expanded>
              Confirm selection
            </Button>
          </DropdownItem>
        )}
      </>
    );
  }

  filterWithSavedQueryPresetsDropdownItems = (filter) => {
    const { multiFilters } = this.state;

    const presetsSelected =
      multiFilters[filter._name] && multiFilters[filter._name].length > 0;

    return (
      <>
        <DropdownItem
          disabled={presetsSelected}
          topSticky
          onClick={() => this.props.onCustomiseFilterOpen(filter.asColumn())}
        >
          Custom
        </DropdownItem>

        <DashboardFilterSavedQueryPresets
          savedQueryPresets={filter.savedQueryPresets}
          filter={filter}
          selectedValues={multiFilters[filter._name] ?? []}
          onUpdate={this.updateMultiFilter}
        />

        {presetsSelected && (
          <DropdownItem
            bottomSticky
            onClick={() => this.submitMultiSelectFilter(filter)}
          >
            <Button size="small" expanded>
              Confirm selection
            </Button>
          </DropdownItem>
        )}
      </>
    );
  };

  dateFilterDropdownItems(f) {
    const buildDropdownItem = (label, aggregation, op, value) => {
      const column = f.asColumn();
      const lhs = AggregateColumn.fromColumn(column.name(), aggregation);
      const newLayer = new LayerWhere(lhs, op, value, null, column.type());
      const action = () => this.props.onApplyFilter(newLayer);

      return (
        <DropdownItem
          key={`dropdown-item-${f.name()}-${value}`}
          onClick={action}
        >
          {label}
        </DropdownItem>
      );
    };

    const menuItems = [
      buildDropdownItem('today', DAY, EQ, TODAY),
      buildDropdownItem('yesterday', DAY, EQ, YESTERDAY),
      buildDropdownItem('this week', WEEK, EQ, THIS_WEEK),
      buildDropdownItem('last week', WEEK, EQ, LAST_WEEK),
      buildDropdownItem('last 7 days', DAY, LESS_THAN, '7 day ago'), // IMPORTANT: expression must be 'day' not 'days' or SQL Server will complain
      buildDropdownItem('this month', MONTH, EQ, THIS_MONTH),
      buildDropdownItem('last month', MONTH, EQ, LAST_MONTH),
      buildDropdownItem('last 30 days', DAY, LESS_THAN, '30 day ago'), // IMPORTANT: expression must be 'day' not 'days' or SQL Server will complain
      buildDropdownItem('this year', YEAR, EQ, THIS_YEAR),
      buildDropdownItem('last year', YEAR, EQ, LAST_YEAR),
    ];

    return (
      <>
        <DropdownItem
          topSticky
          onClick={() => this.props.onCustomiseFilterOpen(f.asColumn())}
        >
          Custom
        </DropdownItem>
        {menuItems}
      </>
    );
  }

  boolFilterDropdownItems(f) {
    const buildDropdownItem = (label, value) => {
      const lhs = AggregateColumn.fromColumn(f.asColumn().name(), null);
      const newLayer = new LayerWhere(lhs, EQ, value, null, f.columnType());
      const action = () => this.props.onApplyFilter(newLayer);

      return (
        <DropdownItem
          key={`dropdown-item-${f.name()}-${value}`}
          onClick={action}
        >
          {label}
        </DropdownItem>
      );
    };

    const menuItems = [
      buildDropdownItem('true', 'TRUE'),
      buildDropdownItem('false', 'FALSE'),
    ];

    return (
      <>
        <DropdownItem
          topSticky
          onClick={() => this.props.onCustomiseFilterOpen(f.asColumn())}
        >
          Custom
        </DropdownItem>
        {menuItems}
      </>
    );
  }

  filterButton(filter) {
    const { appliedFilters, prefetchSavedQueries } = this.props;

    const layerPosition = appliedFilters.findIndex(
      (l) => l.lhs().name() === filter.name(),
    );

    const layer = layerPosition !== -1 ? appliedFilters[layerPosition] : null;

    const type = filter.columnType().type();

    if (layerPosition !== -1) {
      return (
        <div
          className="dashboardFilter position-relative"
          style={{ marginLeft: '5px', cursor: 'auto' }}
          data-testid={DASHBOARD_FILTER}
        >
          {layer.buildContent(() => null, null, true, true)}
          <div
            onClick={() => this.clearFilter(layerPosition, filter.name())}
            className={style.deleteFilter}
          >
            &times;
          </div>
        </div>
      );
    }

    let filterOptions;
    if (type === DATE) {
      filterOptions = this.dateFilterDropdownItems(filter);
    } else if ([UUID, STRING, INT, FLOAT].includes(type)) {
      filterOptions = filter.savedQueryPresets
        ? this.filterWithSavedQueryPresetsDropdownItems(filter)
        : this.filterWithPresetsDropdownItems(filter);
    } else if (type === BOOL) {
      filterOptions = this.boolFilterDropdownItems(filter);
    }

    const handleCloseDropdown = () => {
      // Submit filters if there is any on standby (to be submitted)
      if (filter.name() in this.state.multiFilters) {
        this.submitMultiSelectFilter(filter);
      }
    };

    const withSavedQueryPresets = !!filter.savedQueryPresets;

    return (
      <Island
        size="small"
        disabled={this.props.disabled}
        onMouseEnter={withSavedQueryPresets ? prefetchSavedQueries : undefined}
      >
        <Dropdown onClose={handleCloseDropdown}>
          <Dropdown.Trigger>
            <IslandButton
              content={
                <>
                  {filter.name()}
                  <DropdownArrow />
                </>
              }
            />
          </Dropdown.Trigger>
          <Dropdown.Content top={37}>
            <DropdownList maxHeight={500}>{filterOptions}</DropdownList>
          </Dropdown.Content>
        </Dropdown>

        {!this.props.readonly && (
          <Dropdown>
            <Dropdown.Trigger>
              <IslandButton content={<TrevorIcon name="menu-vertical" />} />
            </Dropdown.Trigger>
            <Dropdown.Content top={37}>
              <DropdownList>
                <DropdownItem
                  onClick={() => this.props.onConfigureFilterOpen(filter)}
                >
                  Configure
                </DropdownItem>
                <DropdownItemWithConfirmation
                  onConfirm={() => this.deleteFilter(filter)}
                >
                  Delete filter
                </DropdownItemWithConfirmation>
              </DropdownList>
            </Dropdown.Content>
          </Dropdown>
        )}
      </Island>
    );
  }

  notifyDeletedFilters() {
    const { filters, appliedFilters } = this.props;
    const filtersRemoved = [];
    for (const value of appliedFilters) {
      const fr = filters.findIndex((f) => value.lhs().name() === f.name());
      if (fr < 0) {
        // Notify that filter was removed
        filtersRemoved.push(value.lhs().name());
      }
    }
    if (filtersRemoved.length > 0) {
      const plurals = filtersRemoved.length === 1 ? ['', 'was'] : ['s', 'were'];
      const message = `The filter${plurals[0]}: ${filtersRemoved.join(', ')} ${
        plurals[1]
      } removed from the main dashboard. Data won\'t be filtered as expected`;
      NotificationManager.error(message, '', 10000);
    }
  }

  filterList() {
    const { filters } = this.props;
    return (
      <FilterListContainer>
        {filters.map((filter) => (
          <div key={filter._id}>{this.filterButton(filter)}</div>
        ))}
      </FilterListContainer>
    );
  }

  deleteFilter(f) {
    const { activeDash } = this.props;
    post(
      'deleting dashboard filter',
      `/dashboards/${activeDash.id()}/filters/${f.id()}/delete`,
    ).then(() => {
      this.props.onReloadFilters();
    });
  }

  isAnyFilterApplied() {
    const { filters, appliedFilters } = this.props;
    for (const value of appliedFilters) {
      const fr = filters.findIndex((f) => value.lhs().name() === f.name());
      if (fr >= 0) {
        return true;
      }
    }
    return false;
  }

  buildShareableLinkWithFiltersBtn() {
    if (this.isAnyFilterApplied()) {
      return (
        <div
          key={`copyLinkDiv-${this.props.appliedFilters.length}`}
          id="copyLinkDiv"
          style={{ borderRadius: '50%' }}
          className={style.notification}
          data-testid={COPY_LINK}
        >
          <CopyIcon
            id="shareRefreshCopyLink"
            tooltip="Copy link with filters applied"
            icon="link"
            isDashboardRefresh
            link={this.props.shareableLinkWithFilters}
          />
        </div>
      );
    }
    return null;
  }

  content() {
    return (
      <div className={` w-100 ${flex.alignItemsCenter}`}>
        <FilterLabel>
          <TrevorIcon name="filter" />
          <Label variant={1}>Filters</Label>
        </FilterLabel>
        {this.filterList()}
        {this.state.filtersLoaded && this.props.filtersLoaded
          ? this.notifyDeletedFilters()
          : null}
        {!this.props.readonly && (
          <button
            className={`${buttons.link} no-wrap`}
            onClick={() => this.props.onConfigureFilterOpen(null)}
            disabled={this.props.disabled}
            style={{ marginLeft: '5px' }}
          >
            + new filter
          </button>
        )}
        {!this.props.readonly && this.buildShareableLinkWithFiltersBtn()}
      </div>
    );
  }

  render() {
    return (
      <div className={style.dashboardFiltersContainer} id="dashFilters">
        {this.props.disabled ? (
          <div
            style={{ cursor: 'not-allowed', opacity: 0.5 }}
            data-tooltip-id="_saveFirst"
          >
            {this.content()}
            <Tooltip id="_saveFirst">
              Please save or discard your changes before using a filter
            </Tooltip>
          </div>
        ) : (
          this.content()
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    activeDash: state.dashboards.active,
    filters: state.dashboards.filters,
    appliedFilters: state.dashboards.appliedFilters,
    filtersLoaded: state.dashboards.filtersLoaded,
  };
};

export { DashboardFilters_LEGACY }; // To be able to unit test the component without testing connect

const DashboardFilters = (props) => {
  const datasourceId = useDatasourceId();
  const refreshId = useSelector(getDashboardRefreshId);
  const prefetchSavedQueries = usePrefetchSavedQueries();

  const shareableLinkWithFilters = `/datasources/${datasourceId}/dashboard-refresh/${refreshId}/open`;
  const [isModalOpen, setIsModalOpen, closeModal] = useModal();
  const [toEditFilter, setToEditFilter] = React.useState(null);
  const [toCustomiseFilter, setToCustomiseFilter] = React.useState(null);
  const dispatch = useAppDispatch();

  const handleConfigureFilterOpen = (filter) => {
    setToCustomiseFilter(null);
    setToEditFilter(filter);
    setIsModalOpen();
  };

  const handleCustomiseFilterOpen = (filter) => {
    setToEditFilter(null);
    setToCustomiseFilter(filter);
    setIsModalOpen();
  };

  const handleApplyFilter = (newFilter) => {
    const { onUpdated, appliedFilters } = props;
    const newAppliedFilters = [...appliedFilters, newFilter];
    onUpdated(newAppliedFilters);
    userAction('use-apply-dashboard-filter');
  };

  const handleCreateOrCustomiseFilter = () => {
    dispatch(reloadDashboardFilters());
    closeModal();
  };

  const handleApplyCustomFilter = (layer) => {
    handleApplyFilter(layer);
    dispatch(modalHidden()); // Reset modal where
    closeModal();
  };

  return (
    <>
      <DashboardFilters_LEGACY
        onReloadFilters={() => dispatch(reloadDashboardFilters())}
        {...props}
        onApplyFilter={handleApplyFilter}
        onConfigureFilterOpen={handleConfigureFilterOpen}
        onCustomiseFilterOpen={handleCustomiseFilterOpen}
        shareableLinkWithFilters={shareableLinkWithFilters}
        prefetchSavedQueries={prefetchSavedQueries}
      />
      {isModalOpen &&
        (toCustomiseFilter ? (
          <DashboardCustomFilterModal
            column={toCustomiseFilter}
            onDone={handleApplyCustomFilter}
          />
        ) : (
          <DashboardFilterModal
            existing={toEditFilter}
            onDone={handleCreateOrCustomiseFilter}
          />
        ))}
    </>
  );
};

export default connect(mapStateToProps)(DashboardFilters);
